import { useDispatch, useSelector } from "react-redux";
import ChatInput from "../../AmplitudeAI/Chat/ChatInput";
import PageDescription from "../../DataAnalyst/sections/PageDescription";
import MixesFileUpload from "./FileUpload";
import { sendMessage } from "../../../../redux/actions";
import { useCallback, useEffect, useState } from "react";
import { showToast } from "../../../../helper/ShowToast";
import { useLocation, useNavigate } from "react-router-dom";
import { PricingPlanModal } from "../../../../components/PricingPlanModal";
import { CreditPlanModal } from "../../../../components/CreditPlanModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PreviewSection from "./PreviewSection";
import StemSplitsLibrary from "./StemSplits/StemSplitsLibrary";
import { clearUploadData, mixPreviewData, uploadAndGetPreview, uploadData } from "../../../../redux/actions/audioEngineer";
import MixesLibrary from "./MixesLibrary/MixesLibrary";
import MastersLibrary from "../MixingEngineer/MastersLibrary/MastersLibrary";
import AppTour from "../../../../components/AppTour/AppTour";
import { mixEnhancerTourSteps } from "../../../../components/AppTour/ModuleTourSteps";

export default function MixEnhancer() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const pageTab = params.get("tab");
    const [runTour, setRunTour] = useState(false)
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [showFileUpload, setShowFileUpload] = useState(true)
    const [showSettings, setShowSettings] = useState(true)
    const [uploadComplete, setUploadComplete] = useState(false);
    const [showStemsLibrary, setShowStemsLibrary] = useState(false)

    const { uploadedData, mixPreview, stemsData } = useSelector((state) => state.audioEngineer);
    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);
    const { showLoad } = useSelector((state) => state?.amplitudeReducer);
    const stemSplitsWithMixOrMaster = (mixPreview?.stemProcessing && Object.entries(mixPreview?.stems || {}).length > 0) && (mixPreview?.isMastered || mixPreview?.isFinalMixed)
    const isMastered = mixPreview?.isMastered && !mixPreview?.stemProcessing
    const isMixed = mixPreview?.isFinalMixed && !mixPreview?.stemProcessing

    const handleUploadNew = () => {
        setShowFileUpload(true)
        dispatch(clearUploadData());
        dispatch(uploadData({}))
        dispatch(mixPreviewData({}))
        navigate('/admin/urstudio/mix-enhancer')
        setUploadComplete(false);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    useEffect(() => {
        if (location.hash === "#start-tour") {
            setRunTour(true)
        }
    }, [location.hash])

    useEffect(() => {
        if (pageTab !== "settings" && showFileUpload && (uploadedData?.data?.[0]?.roexStatus === 'LOCAL_UPLOADING' || uploadedData?.data?.[0]?.roexStatus === 'LOCAL_UPLOADED' || uploadedData?.data?.[0]?.roexStatus === 'UPLOADED_TO_ROEX')) {
            dispatch(clearUploadData());
        }
    }, [dispatch, pageTab])

    const handleFileSelect = useCallback(async (file) => {
        const fileArray = Array.isArray(file) ? file : (file && file.length ? Array.from(file) : [file]);

        // Validate files
        const validFiles = fileArray.filter(f => f && f instanceof File);

        if (validFiles.length === 0) {
            showToast("error", "No valid files selected");
            return;
        }

        dispatch(uploadAndGetPreview(
            toast,
            validFiles,
            setUploadComplete,
            'mix-enhancer',
            false,
            '',
            ''
        ));
    }, [dispatch]);

    const getSettings = (e) => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const handleSend = (input) => {
        if (!hasActiveSubscription) {
            showToast("info", "Please subscribe to continue using Amplitude AI.");
            setShowPricingPlanModal(true);
            return;
        }

        if (!creditDetails?.data || creditDetails?.data?.remaining <= 0) {
            showToast("info", "You are out of credits. Please purchase more to continue.");
            setShowCreditPlanModal(true);
            return;
        }

        dispatch(sendMessage(toast, input));
        navigate("/admin/amplitude-ai");
    };

    const startMixEnhance = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
        setShowFileUpload(false)
        setShowSettings(true)
    }

    useEffect(() => {
        if (pageTab === "settings") {
            setShowFileUpload(false)
            setShowSettings(true)
        }
    }, [pageTab])

    const onExit = () => {
        setShowFileUpload(true)
        dispatch(clearUploadData());
        dispatch(uploadData({}))
        dispatch(mixPreviewData({}))
        navigate('/admin/urstudio')
        setUploadComplete(false);
    }

    return (
        <div className="DataAnalyst-page py-5">
            <AppTour
                tourSteps={mixEnhancerTourSteps}
                runTour={runTour}
                setRunTour={setRunTour}
            />
            <div className="container max-w-full">
                <PageDescription
                    title={location.hash === "#start-tour" ? "Your Mix Enhancer - Demo Mode" : "Your Mix Enhancer"}
                    description="Enhance your mixes in minutes — powered by intelligent, genre-aware AI."
                />
                {showFileUpload &&
                    <div className="mt-4 p-4 md:p-8 bg-theme_clr1 rounded-2xl">
                        <MixesFileUpload onFileSelect={handleFileSelect} />
                        {uploadedData?.data?.length > 0 && uploadedData?.data?.[0]?.operation === 'mix-enhancer' && (
                            <div className="flex flex-col w-[40%] mx-auto items-center mt-4 space-y-3">
                                {uploadedData.data.map((file, index) => {

                                    let progress = 30;
                                    let label = "30%";

                                    if (file.roexStatus === "LOCAL_UPLOADING") {
                                        progress = 15;
                                        label = "15%";
                                    }

                                    if (file.roexStatus === "QUEUED_FOR_ROEX") {
                                        progress = 40;
                                        label = "40%";
                                    }

                                    if (file.roexStatus === "UPLOADING_TO_ROEX") {
                                        progress = 60;
                                        label = "60%";
                                    }

                                    if (file.roexStatus === "UPLOADED_TO_ROEX") {
                                        progress = 100;
                                        label = "100%";
                                    }

                                    if (file.roexStatus === "FAILED") {
                                        progress = 100;
                                        label = "Failed";
                                    }

                                    return (
                                        <div key={index} className="w-full">
                                            <div className="flex justify-between text-sm text-white mb-1">
                                                <span className="truncate max-w-[70%]">
                                                    {file.filename}
                                                </span>
                                                <span>{label}</span>
                                            </div>

                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full transition-all duration-700 ${file.roexStatus === "FAILED"
                                                        ? "bg-red-500"
                                                        : `${file.roexStatus === "UPLOADED_TO_ROEX" ? "bg-theme_clr2" : "bg-theme_clr2 animated-pulse"}`
                                                        }`}
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>

                                            {file.roexStatus === "UPLOADED_TO_ROEX" && (
                                                <button
                                                    onClick={startMixEnhance}
                                                    className="mt-6 w-full bg-theme_clr2 text-white py-3 rounded-xl hover:bg-[#ff724d] transition-colors font-medium"
                                                >
                                                    Start Mix Enhance
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                }
                {location.hash === "#start-tour" &&
                    <div className="mt-4 p-4 md:p-8 bg-theme_clr1 rounded-2xl" data-tour="preview-section">
                        <PreviewSection demoMode isMastered={(e) => setShowStemsLibrary(e)} />
                    </div>
                }
                {!showFileUpload && showSettings &&
                    <div className="mt-4 p-4 md:p-8 bg-theme_clr1 rounded-xl">
                        {(isMixed || isMastered || stemSplitsWithMixOrMaster) && (
                            <div className="mx-auto">
                                <div className="bg-white mb-4 rounded-xl px-4 py-2 w-full">
                                    <button onClick={onExit} className="bg-theme_clr2 bg-opacity-50 text-theme_clr4 font-medium text-sm rounded-md px-4 py-2"><i className="bi bi-arrow-left-square mr-1"></i> Exit</button>
                                </div>
                            </div>
                        )}
                        <PreviewSection isMastered={(e) => setShowStemsLibrary(e)} />
                    </div>
                }

                <div className="mt-4" data-tour="amplitude-ai-chat">
                    <ChatInput
                        title={'Plan your next move with your enhanced tracks.'}
                        inputPlaceholder={"Let Amplitude AI design the perfect release strategy."}
                        onSend={handleSend}
                        isDisabled={showLoad}
                    />
                </div>
                <div className="bg-theme_clr1 text-white rounded-xl p-4 mt-4" data-tour="mixes-library">
                    {stemSplitsWithMixOrMaster
                        ? <StemSplitsLibrary
                            addNew={handleUploadNew}
                            handleSettings={getSettings}
                            trackId={mixPreview?._id}
                            data={stemsData}
                        />
                        : isMixed
                            ? <MixesLibrary addNew={handleUploadNew} handleSettings={getSettings} />
                            : isMastered
                                ? <MastersLibrary addNew={handleUploadNew} handleSettings={getSettings} showHeader />
                                : <MixesLibrary addNew={handleUploadNew} handleSettings={getSettings} />
                    }
                </div>
                {location.hash === "#start-tour" &&
                    <>
                        <div className="rounded-xl mt-4" data-tour="stems-library">
                            <img
                                className="w-full h-auto object-cover"
                                src={require('../../../../assets/images/stem-splits.png')}
                            />
                        </div>
                        <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 rounded p-4">
                            <p className="text-blue-900 font-semibold mb-2">ℹ️ Demo Mode</p>
                            <p className="text-blue-800 text-sm">
                                This is a demonstration page showing the Mix Enhancer interface. All data is simulated for demo purposes.
                            </p>
                        </div>
                    </>
                }
            </div>
            <PricingPlanModal
                isOpen={showPricingPlanModal}
                onClose={() => setShowPricingPlanModal(false)}
                returnUrl={window.location.origin + "/admin/urstudio/mix-enhancer"}
            />
            <CreditPlanModal
                isOpen={showCreditPlanModal}
                onClose={() => setShowCreditPlanModal(false)}
            />
        </div>
    )
}